import { Button, SxProps, useTheme, Box, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import investmentKpiLang from '../../../../../langs/investmentKpiLang';
import { useTranslation } from 'react-i18next';
import investmentTypeDomainLang from '../../../langs/investmentTypeDomainLang';
import _ from 'lodash';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import styleVariables from '../../../../../../../app/styles/styleVariables';
import investmentTypeLang from '../../../langs/investmentTypeLang';
import am5locales_en_US from '@amcharts/amcharts5/locales/en_US';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';
import { useAppSettings } from '../../../../../../../app/features/settings/providers/AppSettingsProvider';
import AppLoaderGraph from '../../../../../../../app/features/loader/graph/AppLoaderGraph';
import useAppFormatAmount from '../../../../../../../app/features/format/hooks/useAppFormatAmount';
import useAppFormatNumber from '../../../../../../../app/features/format/hooks/useAppFormatNumber';
import { InfoRounded } from '@mui/icons-material';
import IFund from '../../../../../interfaces/fund/IFund';
import IFundTypeStatsNavVintage from '../../../../../interfaces/IFundTypeStatsNavVintage';
import IFundTypeStatsNavSFDR from '../../../../../interfaces/expertise/IFundTypeStatsNavSFDR';
import IFundTypeStatsNavDomain from '../../../../../interfaces/expertise/IFundTypeStatsNavDomain';
import { TInvestFundCurrency } from '../../../../../types/fund/TInvestFundCurrency';
import CGraphMultiCurrency from '../../../../../components/CGraphMultiCurrency';
import { CCardStatsChart } from '@/app/components/Card/Stats/CCardStatsChart';

type InvestmentPortfolioNavProps = {
	allFunds: IFund[];
	netAssetValue: number;
	portfolioNavVintages: IFundTypeStatsNavVintage[];
	portfolioNavSfdrs: IFundTypeStatsNavSFDR[];
	portfolioNavDomains: IFundTypeStatsNavDomain[];
	displayNavNc: boolean;
	currency: TInvestFundCurrency;
};

const CGraphPortfolioNav = (props: InvestmentPortfolioNavProps) => {
	const {
		allFunds,
		netAssetValue,
		portfolioNavVintages,
		portfolioNavSfdrs,
		portfolioNavDomains,
		displayNavNc,
		currency,
	} = props;

	const theme = useTheme();
	const { t, i18n } = useTranslation();

	const { rounding } = useAppSettings();
	const { displayRounding, displayRoundingSuffix, displayCurrencySymbol } = useAppFormatAmount();
	const { formatAmount } = useAppFormatNumber();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const [modeSelected, setModeSelected] = useState<'vintage' | 'sfdr' | 'domain'>('domain');

	const darkModeEnabled = theme.palette.mode === 'dark';

	const listColors = !darkModeEnabled
		? styleVariables.sliceColors.lightMode.map((x) => am5.color(x))
		: styleVariables.sliceColors.darkMode.map((x) => am5.color(x));

	useEffect(() => {
		if (modeSelected === 'vintage' && portfolioNavVintages && !displayNavNc && currency) {
			// Vintage chart
			const vintageSynthesis = am5.Root.new('fund-type-synthesis-chart');
			darkModeEnabled
				? vintageSynthesis.setThemes([
						am5themes_Dark.new(vintageSynthesis),
						am5themes_Responsive.new(vintageSynthesis),
				  ])
				: vintageSynthesis.setThemes([
						am5themes_Animated.new(vintageSynthesis),
						am5themes_Responsive.new(vintageSynthesis),
				  ]);

			if (i18n.language === 'fr') {
				vintageSynthesis.locale = am5locales_fr_FR;
			} else {
				vintageSynthesis.locale = am5locales_en_US;
			}

			const chartVintage = vintageSynthesis.container.children.push(
				am5percent.PieChart.new(vintageSynthesis, {
					layout: vintageSynthesis.verticalLayout,
					innerRadius: am5.percent(85),
				}),
			);

			const seriesVintage = chartVintage.series.push(
				am5percent.PieSeries.new(vintageSynthesis, {
					valueField: 'value',
					categoryField: 'category',
				}),
			);

			chartVintage.seriesContainer.children.push(
				am5.Label.new(vintageSynthesis, {
					textAlign: 'center',
					y: 26,
					centerY: am5.percent(100),
					fill: theme.palette.mode === 'dark' ? am5.color('#FFF') : am5.color('#1A2B48'),
					centerX: am5.p50,
					text: `[bold fontSize:26px]${formatAmount(
						displayRounding(netAssetValue, rounding),
						2,
						true,
					)} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(currency)}[/][/]`,
				}),
			);

			const sliceTemplate = seriesVintage.slices.template;
			sliceTemplate.setAll({
				templateField: 'settings',
				cornerRadius: 30,
			});

			sliceTemplate.set('toggleKey', 'none');
			sliceTemplate.states.create('active', {
				shiftRadius: 0,
			});

			const sliceTicksTemplate = seriesVintage.ticks.template;
			sliceTicksTemplate.setAll({
				templateField: 'tickSettings',
			});

			const sliceLabelsTemplate = seriesVintage.labels.template;
			sliceLabelsTemplate.setAll({
				templateField: 'labelSettings',
			});

			const sliceTooltipsTemplate = seriesVintage.slices.template;
			sliceTooltipsTemplate.setAll({
				templateField: 'tooltipSettings',
			});

			seriesVintage.data.setAll(
				portfolioNavVintages.map((portfolioNavVintage, index) => ({
					category: portfolioNavVintage.year.toString(),
					value: _.sumBy(portfolioNavVintage.kpisByFund, 'kpis.netAssetValue'),
					ratio: _.round(
						(_.sumBy(portfolioNavVintage.kpisByFund, 'kpis.netAssetValue') / netAssetValue) * 100,
						1,
					),
					settings: {
						fill: listColors[index % listColors.length],
						stroke: 0,
					},
					tickSettings: {
						stroke: listColors[index % listColors.length],
						strokeOpacity: 1,
					},
					labelSettings: {
						fill: listColors[index % listColors.length],
						fontSize: 15,
						fontWeight: 600,
						text: `[bold fontSize:17px]{category}[/]\n{ratio}%`,
					},
					tooltipSettings: {
						fill: listColors[index % listColors.length],
						stroke: listColors[index % listColors.length],

						tooltipText: portfolioNavVintage.kpisByFund
							.map(
								(f) =>
									`${f.fund.name} (${formatAmount(
										displayRounding(f.kpis.netAssetValue ?? 0, rounding),
										2,
										true,
									)} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(currency)})`,
							)
							.join('\n'),
					},
				})),
			);

			const legendVintageRoot = am5.Root.new('fund-type-legend');
			const legendVintage = legendVintageRoot.container.children.push(
				am5.Legend.new(legendVintageRoot, {
					centerX: am5.percent(50),
					x: am5.percent(50),
					layout: legendVintageRoot.verticalLayout,
					useDefaultMarker: true,
					height: am5.percent(100),
					verticalScrollbar: am5.Scrollbar.new(legendVintageRoot, {
						orientation: 'vertical',
					}),
				}),
			);
			legendVintage.markerRectangles.template.setAll({
				cornerRadiusTL: 10,
				cornerRadiusTR: 10,
				cornerRadiusBL: 10,
				cornerRadiusBR: 10,
			});
			legendVintage.labels.template.setAll({
				fontSize: 14,
				fontWeight: '300',
				fill: am5.color(theme.palette.primary.main),
			});
			legendVintage.valueLabels.template.setAll({
				fontSize: 14,
				fontWeight: '400',
				fill: am5.color(theme.palette.primary.main),
			});

			legendVintage.data.setAll(seriesVintage.dataItems);

			// Play initial series animation
			seriesVintage.appear(1000, 100);

			return () => {
				vintageSynthesis.dispose();
				legendVintageRoot.dispose();
			};
		}
	}, [
		darkModeEnabled,
		modeSelected,
		portfolioNavVintages,
		netAssetValue,
		t,
		rounding,
		displayNavNc,
		i18n.language,
		theme.palette.mode,
		theme.palette.primary.main,
		listColors,
		formatAmount,
		displayRounding,
		displayRoundingSuffix,
		displayCurrencySymbol,
		currency,
	]);

	useEffect(() => {
		if (modeSelected === 'sfdr' && portfolioNavSfdrs && !displayNavNc && currency) {
			// ESG Strategy chart
			const sfdrSynthesis = am5.Root.new('fund-type-synthesis-chart');
			darkModeEnabled
				? sfdrSynthesis.setThemes([am5themes_Dark.new(sfdrSynthesis), am5themes_Responsive.new(sfdrSynthesis)])
				: sfdrSynthesis.setThemes([
						am5themes_Animated.new(sfdrSynthesis),
						am5themes_Responsive.new(sfdrSynthesis),
				  ]);

			if (i18n.language === 'fr') {
				sfdrSynthesis.locale = am5locales_fr_FR;
			} else {
				sfdrSynthesis.locale = am5locales_en_US;
			}

			const chartSfdr = sfdrSynthesis.container.children.push(
				am5percent.PieChart.new(sfdrSynthesis, {
					layout: sfdrSynthesis.verticalLayout,
					innerRadius: am5.percent(85),
				}),
			);

			const seriesSfdr = chartSfdr.series.push(
				am5percent.PieSeries.new(sfdrSynthesis, {
					valueField: 'value',
					categoryField: 'category',
				}),
			);

			chartSfdr.seriesContainer.children.push(
				am5.Label.new(sfdrSynthesis, {
					textAlign: 'center',
					y: 26,
					centerY: am5.percent(100),
					fill: theme.palette.mode === 'dark' ? am5.color('#FFF') : am5.color('#1A2B48'),
					centerX: am5.p50,
					text: `[bold fontSize:26px]${formatAmount(
						displayRounding(netAssetValue, rounding),
						2,
						true,
					)} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(currency)}[/][/]`,
				}),
			);

			const sliceSfdrTemplate = seriesSfdr.slices.template;
			sliceSfdrTemplate.setAll({
				templateField: 'settings',
				cornerRadius: 30,
			});

			sliceSfdrTemplate.set('toggleKey', 'none');
			sliceSfdrTemplate.states.create('active', {
				shiftRadius: 0,
			});

			const sliceTicksSfdrTemplate = seriesSfdr.ticks.template;
			sliceTicksSfdrTemplate.setAll({
				templateField: 'tickSettings',
			});

			const sliceLabelsSfdrTemplate = seriesSfdr.labels.template;
			sliceLabelsSfdrTemplate.setAll({
				templateField: 'labelSettings',
			});

			const sliceTooltipsSfdrTemplate = seriesSfdr.slices.template;
			sliceTooltipsSfdrTemplate.setAll({
				templateField: 'tooltipSettings',
			});

			seriesSfdr.data.setAll(
				portfolioNavSfdrs.map((portfolioNavSfdr, index) => ({
					value: _.sumBy(portfolioNavSfdr.kpisByFund, 'kpis.netAssetValue'),
					category: portfolioNavSfdr.name,
					ratio: _.round(
						(_.sumBy(portfolioNavSfdr.kpisByFund, 'kpis.netAssetValue') / netAssetValue) * 100,
						1,
					),
					settings: {
						fill: listColors[index % listColors.length],
						stroke: 0,
					},
					tickSettings: {
						stroke: listColors[index % listColors.length],
						strokeOpacity: 1,
					},
					labelSettings: {
						fill: listColors[index % listColors.length],
						fontSize: 15,
						fontWeight: 600,
						text: `[bold fontSize:17px]{category}[/]\n{ratio}%`,
					},
					tooltipSettings: {
						fill: listColors[index % listColors.length],
						stroke: listColors[index % listColors.length],

						tooltipText: portfolioNavSfdr.kpisByFund
							.map(
								(f) =>
									`${f.fund.name} (${formatAmount(
										displayRounding(f.kpis.netAssetValue ?? 0, rounding),
										2,
										true,
									)} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(currency)})`,
							)
							.join('\n'),
					},
				})),
			);

			const legendSfdrRoot = am5.Root.new('fund-type-legend');
			const legendSfdr = legendSfdrRoot.container.children.push(
				am5.Legend.new(legendSfdrRoot, {
					centerX: am5.percent(50),
					x: am5.percent(50),
					layout: legendSfdrRoot.verticalLayout,
					useDefaultMarker: true,
					height: am5.percent(100),
					verticalScrollbar: am5.Scrollbar.new(legendSfdrRoot, {
						orientation: 'vertical',
					}),
				}),
			);
			legendSfdr.markerRectangles.template.setAll({
				cornerRadiusTL: 10,
				cornerRadiusTR: 10,
				cornerRadiusBL: 10,
				cornerRadiusBR: 10,
			});
			legendSfdr.labels.template.setAll({
				fontSize: 14,
				fontWeight: '300',
				fill: am5.color(theme.palette.primary.main),
			});
			legendSfdr.valueLabels.template.setAll({
				fontSize: 14,
				fontWeight: '400',
				fill: am5.color(theme.palette.primary.main),
			});

			legendSfdr.data.setAll(seriesSfdr.dataItems);

			// Play initial series animation
			// https://www.amcharts.com/docs/v5/concepts/animations/#Animation_of_series
			seriesSfdr.appear(1000, 100);

			return () => {
				sfdrSynthesis.dispose();
				legendSfdrRoot.dispose();
			};
		}
	}, [
		darkModeEnabled,
		modeSelected,
		portfolioNavSfdrs,
		netAssetValue,
		t,
		displayNavNc,
		rounding,
		i18n.language,
		theme.palette.mode,
		theme.palette.primary.main,
		listColors,
		formatAmount,
		displayRounding,
		displayRoundingSuffix,
		displayCurrencySymbol,
		currency,
	]);

	useEffect(() => {
		if (modeSelected === 'domain' && portfolioNavDomains && !displayNavNc && currency) {
			// Synthesis chart 3
			const domainSynthesis = am5.Root.new('fund-type-synthesis-chart');
			darkModeEnabled
				? domainSynthesis.setThemes([
						am5themes_Dark.new(domainSynthesis),
						am5themes_Responsive.new(domainSynthesis),
				  ])
				: domainSynthesis.setThemes([
						am5themes_Animated.new(domainSynthesis),
						am5themes_Responsive.new(domainSynthesis),
				  ]);

			if (i18n.language === 'fr') {
				domainSynthesis.locale = am5locales_fr_FR;
			} else {
				domainSynthesis.locale = am5locales_en_US;
			}

			const chartDomain = domainSynthesis.container.children.push(
				am5percent.PieChart.new(domainSynthesis, {
					layout: domainSynthesis.verticalLayout,
					innerRadius: am5.percent(85),
				}),
			);

			const seriesDomain = chartDomain.series.push(
				am5percent.PieSeries.new(domainSynthesis, {
					valueField: 'value',
					categoryField: 'category',
					alignLabels: true,
				}),
			);

			chartDomain.seriesContainer.children.push(
				am5.Label.new(domainSynthesis, {
					textAlign: 'center',
					y: 26,
					centerY: am5.percent(100),
					fill: theme.palette.mode === 'dark' ? am5.color('#FFF') : am5.color('#1A2B48'),
					centerX: am5.p50,
					text: `[bold fontSize:26px]${formatAmount(
						displayRounding(netAssetValue, rounding),
						2,
						true,
					)} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(currency)}[/][/]`,
				}),
			);

			const sliceDomainTemplate = seriesDomain.slices.template;
			sliceDomainTemplate.setAll({
				templateField: 'settings',
				cornerRadius: 30,
			});

			sliceDomainTemplate.set('toggleKey', 'none');
			sliceDomainTemplate.states.create('active', {
				shiftRadius: 0,
			});

			const sliceTicksDomainTemplate = seriesDomain.ticks.template;
			sliceTicksDomainTemplate.setAll({
				templateField: 'tickSettings',
			});

			const sliceLabelsDomainTemplate = seriesDomain.labels.template;
			sliceLabelsDomainTemplate.setAll({
				templateField: 'labelSettings',
				maxWidth: 150,
				oversizedBehavior: 'wrap',
			});

			const sliceTooltipsDomainTemplate = seriesDomain.slices.template;
			sliceTooltipsDomainTemplate.setAll({
				templateField: 'tooltipSettings',
			});

			seriesDomain.data.setAll(
				portfolioNavDomains.map((portfolioNavDomain, index) => ({
					value: _.sumBy(portfolioNavDomain.kpisByFund, 'kpis.netAssetValue'),
					ratio: _.round(
						(_.sumBy(portfolioNavDomain.kpisByFund, 'kpis.netAssetValue') / netAssetValue) * 100,
						1,
					),
					category: investmentTypeDomainLang[portfolioNavDomain.tag]
						? t(investmentTypeDomainLang[portfolioNavDomain.tag].tag)
						: portfolioNavDomain.tag,
					settings: {
						fill: listColors[index % listColors.length],

						stroke: 0,
					},
					tickSettings: {
						stroke: listColors[index % listColors.length],

						strokeOpacity: 1,
					},
					labelSettings: {
						fill: listColors[index % listColors.length],
						fontSize: 15,
						fontWeight: 600,

						text: `[bold fontSize:12px]{category}[/]\n{ratio}%`,
					},
					tooltipSettings: {
						fill: listColors[index % listColors.length],
						stroke: listColors[index % listColors.length],

						tooltipText: portfolioNavDomain.kpisByFund
							.map(
								(f) =>
									`${f.fund.name} (${formatAmount(
										displayRounding(f.kpis.netAssetValue ?? 0, rounding),
										2,
										true,
									)} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(currency)})`,
							)
							.join('\n'),
					},
				})),
			);

			const legendDomainRoot = am5.Root.new('fund-type-legend');
			const legendDomain = legendDomainRoot.container.children.push(
				am5.Legend.new(legendDomainRoot, {
					centerX: am5.percent(50),
					x: am5.percent(50),
					layout: legendDomainRoot.verticalLayout,
					useDefaultMarker: true,
					height: am5.percent(100),
					verticalScrollbar: am5.Scrollbar.new(legendDomainRoot, {
						orientation: 'vertical',
					}),
				}),
			);
			legendDomain.markerRectangles.template.setAll({
				cornerRadiusTL: 10,
				cornerRadiusTR: 10,
				cornerRadiusBL: 10,
				cornerRadiusBR: 10,
			});
			legendDomain.labels.template.setAll({
				fontSize: 14,
				fontWeight: '300',
				fill: am5.color(theme.palette.primary.main),
			});
			legendDomain.valueLabels.template.setAll({
				fontSize: 14,
				fontWeight: '400',
				fill: am5.color(theme.palette.primary.main),
			});

			legendDomain.data.setAll(seriesDomain.dataItems);

			// Play initial series animation
			// https://www.amcharts.com/docs/v5/concepts/animations/#Animation_of_series
			seriesDomain.appear(1000, 100);

			return () => {
				domainSynthesis.dispose();
				legendDomainRoot.dispose();
			};
		}
	}, [
		darkModeEnabled,
		modeSelected,
		portfolioNavDomains,
		netAssetValue,
		rounding,
		displayNavNc,
		i18n.language,
		theme.palette.mode,
		theme.palette.primary.main,
		t,
		listColors,
		formatAmount,
		displayRounding,
		displayRoundingSuffix,
		displayCurrencySymbol,
		currency,
	]);

	const sxChip = {
		backgroundColor: 'transparent',
		color: theme.palette.primary.main,
		borderColor: 'white',
		borderWidth: '1px',
		borderStyle: 'solid',
		fontSize: { sm: '1rem', xs: '0.85rem' },
		fontWeight: '400',
		lineHeight: '1.5',
		padding: { sm: '8px 20px', xs: '8px 14px' },
		borderRadius: '80px',
		margin: '0px 8px',
		'&.active': {
			backgroundColor: darkModeEnabled ? '#E3E8EF' : theme.palette.primary.main,
			color: darkModeEnabled ? styleVariables.primaryColor : theme.palette.primary.contrastText,
			fontWeight: '600',
			'& :hover': {
				backgroundColor: theme.palette.primary.contrastText,
			},
		},
	} as SxProps;

	// Liste des fonds n'ayant pas de NAV sur la période de reporting
	const [withoutNetAssetValueFunds, setWithoutNetAssetValueFunds] = useState<IFund[] | null>(null);
	useEffect(() => {
		if (portfolioNavVintages) {
			setWithoutNetAssetValueFunds(
				_.uniqBy(
					_.flatten(portfolioNavVintages.map((x) => x.kpisByFund)).filter(
						(x) => x.kpis.netAssetValue === null,
					),
					'fund.id',
				).map((x) => x.fund),
			);
		}
	}, [portfolioNavVintages]);

	return (
		<CCardStatsChart
			title={t(investmentTypeLang.portfolio_nav.tag)}
			chartComponent={
				displayNavNc ? (
					<>
						<Box
							sx={{
								width: '100%',
								height: { xs: '250px', sm: '330px' },
								paddingTop: '50px',
								marginBottom: '30px',
							}}
						>
							<Box>
								{t(investmentTypeLang.text_for_graph_with_nav_nc.tag)} :<br />
								<strong> {withoutNetAssetValueFunds?.map((f) => f.name).join(', ')}</strong>
							</Box>
							{
								// Si on a autant de fonds existants que de fonds dont la nav est NC on n'affiche pas le message en dessous qui explique que l'on peut retirer des fonds des filtres
								withoutNetAssetValueFunds &&
									allFunds &&
									withoutNetAssetValueFunds.length !== allFunds.length && (
										<Box sx={{ marginTop: '30px' }}>
											<InfoRounded sx={{ height: '1.25rem' }} />{' '}
											{t(investmentTypeLang.text_for_graph_with_nav_nc_complement.tag)}
										</Box>
									)
							}
						</Box>
					</>
				) : displayNavNc !== undefined && !displayNavNc ? (
					<>
						{currency ? (
							<Box
								id="fund-type-synthesis-chart"
								sx={{ width: '100%', height: { xs: '250px', sm: '330px' }, marginBottom: '30px' }}
							/>
						) : (
							<CGraphMultiCurrency height={isMobile ? 250 : 330} />
						)}
						<Box
							id="fund-type-legend"
							sx={{ height: '100px', display: { xs: 'block', sm: 'none' }, marginBottom: '20px' }}
						/>
						<Box>
							<Button
								id="fund-type-legend-domain"
								sx={sxChip}
								className={modeSelected === 'domain' ? 'active' : ''}
								onClick={() => setModeSelected('domain')}
							>
								{t(investmentKpiLang.domain.tag)}
							</Button>
							<Button
								id="fund-type-legend-vintage"
								sx={sxChip}
								className={modeSelected === 'vintage' ? 'active' : ''}
								onClick={() => setModeSelected('vintage')}
							>
								{t(investmentKpiLang.vintage.tag)}
							</Button>
							<Button
								id="fund-type-legend-sfdr"
								sx={sxChip}
								className={modeSelected === 'sfdr' ? 'active' : ''}
								onClick={() => setModeSelected('sfdr')}
							>
								{t(investmentKpiLang.sfdr.tag)}
							</Button>
						</Box>
					</>
				) : (
					<AppLoaderGraph />
				)
			}
		/>
	);
};

export default CGraphPortfolioNav;
